// Typed SDK errors that callers (and UIs) branch on.
//
// Most SDK failures surface as plain `Error` with an AWS-shaped message —
// fine for logging, useless for control flow. `SessionExpiredError` is the
// one error apps are expected to *recognize* and react to: the user's
// OIDC session can no longer mint AWS credentials and there is no silent
// way to recover (the refresh token is gone, expired, or revoked). The
// only fix is a fresh sign-in, so the UI should prompt for one instead of
// rendering the raw `cognito-identity ... 400 ... Token expired` string.

export type SessionExpiredReason =
  /** A refresh was attempted but the token endpoint rejected it. */
  | "refresh-failed"
  /** No refresh_token was cached, so no silent refresh was even possible. */
  | "no-refresh-token"
  /** AWS rejected the (freshly-refreshed) token at the STS/Identity-Pool exchange. */
  | "token-rejected";

export class SessionExpiredError extends Error {
  override readonly name = "SessionExpiredError";
  readonly reason: SessionExpiredReason;

  constructor(reason: SessionExpiredReason, options?: { cause?: unknown }) {
    super("Your session has expired — sign in again.", options);
    this.reason = reason;
  }
}

/** Narrowing helper so callers don't have to import the class just to test.
 *  Matches on `name` as well as instanceof, so it works across module-instance
 *  boundaries (an SDK loaded via a separate dynamic import). */
export function isSessionExpiredError(e: unknown): e is SessionExpiredError {
  return (
    e instanceof SessionExpiredError ||
    (e instanceof Error && e.name === "SessionExpiredError")
  );
}

/** The Identity Pool DENIED credentials for a perfectly valid token — the
 *  `AmbiguousRoleResolution: "Deny"` gate (a signed-in user with no
 *  `{slug}:operator-*` / owner / admin group for this project). Distinct from
 *  `SessionExpiredError` on purpose: re-signing-in as the SAME account won't
 *  help, so UIs must show "not authorized" (offer an account switch), never
 *  the "session expired" re-login loop. */
export class NotAuthorizedError extends Error {
  override readonly name = "NotAuthorizedError";
  /** Stable machine-readable discriminator (survives cross-realm instanceof
   *  failures — e.g. an SDK loaded via a separate dynamic import). */
  readonly code = "identity-pool-denied";
}

/** Narrowing helper. Matches on the stable `code` as well as instanceof, so it
 *  works across module-instance boundaries (dynamic SDK imports). */
export function isNotAuthorizedError(e: unknown): e is NotAuthorizedError {
  return (
    e instanceof NotAuthorizedError ||
    (e instanceof Error && (e as { code?: unknown }).code === "identity-pool-denied")
  );
}
